import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Users, Calendar, User, ExternalLink, MapPin,
  CheckCircle, XCircle, Clock, Building2, Layers,
  Image, FileText, Globe, Download, Copy, Eye
} from "lucide-react";
import { getPublicUrlAsync, getProjectShareUrlAsync, safeCopyToClipboard } from "../utils/shareHelper";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Helper to normalize upload URLs - handles both old (/uploads/...) and new (/api/uploads/...) formats
function normalizeUploadUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  // Ensure path starts with /api/uploads
  if (path.startsWith('/uploads/')) {
    return `${API_URL}/api${path}`;
  }
  if (path.startsWith('/api/uploads/')) {
    return `${API_URL}${path}`;
  }
  return `${API_URL}${path}`;
}

export default function ClientProjectView() {
  const { token } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [notes, setNotes] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/share/project/${token}`);
      if (response.ok) {
        const data = await response.json();
        setProject(data);
        const initialNotes = {};
        data.roles?.forEach(role => {
          role.candidates?.forEach(c => {
            initialNotes[c.id] = c.client_notes || "";
          });
        });
        setNotes(initialNotes);
      } else if (response.status === 404) {
        setError("This link is invalid or has expired.");
      } else {
        setError("Failed to load project.");
      }
    } catch (err) {
      setError("Failed to load project.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const handleFeedback = async (optionId, status) => {
    setSubmitting(prev => ({ ...prev, [optionId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/share/project/${token}/candidate/${optionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_status: status,
          client_notes: notes[optionId] || null
        })
      });

      if (response.ok) {
        toast.success(`Marked as ${status}`, { duration: 1500 });
        setProject(prev => ({
          ...prev,
          roles: prev.roles.map(role => ({
            ...role,
            candidates: role.candidates.map(c => 
              c.id === optionId ? { ...c, client_status: status, client_notes: notes[optionId] } : c
            )
          }))
        }));
        setExpandedNotes(prev => ({ ...prev, [optionId]: false }));
      } else {
        toast.error("Failed to save");
      }
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSubmitting(prev => ({ ...prev, [optionId]: false }));
    }
  };

  const copyLink = async () => {
    // Use OG-enabled share URL for link previews on WhatsApp/Telegram
    const url = await getProjectShareUrlAsync(token);
    const success = await safeCopyToClipboard(url);
    if (success) {
      setLinkCopied(true);
      toast.success("Link copied");
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      toast.error("Failed to copy");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric"
      });
    } catch { return dateStr; }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F6F7' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-neutral-900 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-neutral-500 text-sm tracking-wide">Loading project...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F6F7' }}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-6 h-6 text-neutral-400" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-1">Link Not Found</h2>
          <p className="text-neutral-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  const totalOptions = project.roles?.reduce((sum, r) => sum + (r.candidates?.length || 0), 0) || 0;
  const selectedCount = project.roles?.reduce((sum, r) => 
    sum + (r.candidates?.filter(c => c.client_status === "Selected").length || 0), 0) || 0;
  const rejectedCount = project.roles?.reduce((sum, r) => 
    sum + (r.candidates?.filter(c => c.client_status === "Rejected").length || 0), 0) || 0;
  const pendingCount = totalOptions - selectedCount - rejectedCount;
  const progressPercent = totalOptions > 0 ? Math.round(((selectedCount + rejectedCount) / totalOptions) * 100) : 0;

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{
        backgroundColor: '#F5F6F7',
        backgroundImage: `
          linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px'
      }}
    >
      {/* Main Container */}
      <div className="max-w-5xl mx-auto">
        <div 
          className="bg-white rounded-[20px] overflow-hidden"
          style={{ 
            boxShadow: '0 4px 40px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)'
          }}
        >
          {/* ========== PROJECT HEADER ========== */}
          <div className="px-8 pt-8 pb-6">
            {/* Top Row: Logo + Company */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="TalentMeet" className="w-9 h-9 rounded-lg" />
                <div className="h-6 w-px bg-neutral-200"></div>
                <span className="text-xs font-medium text-neutral-400 uppercase tracking-widest">Project Board</span>
              </div>
              <button
                onClick={copyLink}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                {linkCopied ? "Copied" : "Share Link"}
              </button>
            </div>

            {/* Title + Subtitle */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-neutral-900 tracking-tight mb-1" style={{ fontFamily: 'Georgia, serif' }}>
                {project.title}
              </h1>
              {project.description ? (
                <p className="text-neutral-500 text-sm">{project.description}</p>
              ) : (
                <p className="text-neutral-400 text-sm">Project by {project.enterprise_name || "Enterprise"}</p>
              )}
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-neutral-400" />
                  <span className="text-sm"><strong className="text-neutral-900">{project.roles?.length || 0}</strong> <span className="text-neutral-500">Requirements</span></span>
                </div>
                <div className="h-4 w-px bg-neutral-200"></div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-neutral-400" />
                  <span className="text-sm"><strong className="text-neutral-900">{totalOptions}</strong> <span className="text-neutral-500">Options</span></span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-3 flex-1 min-w-[200px] max-w-xs">
                <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${progressPercent}%`,
                      background: progressPercent === 100 
                        ? 'linear-gradient(90deg, #22c55e, #16a34a)' 
                        : 'linear-gradient(90deg, #fbbf24, #f59e0b)'
                    }}
                  ></div>
                </div>
                <span className="text-sm font-semibold text-neutral-900">{progressPercent}%</span>
              </div>

              {project.deadline && (
                <div className="flex items-center gap-2 text-sm text-amber-600">
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">Due {formatDate(project.deadline)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-neutral-100"></div>

          {/* ========== REQUIREMENTS CONTENT ========== */}
          <div className="p-8">
            {project.roles?.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                  <Layers className="w-6 h-6 text-neutral-400" />
                </div>
                <h3 className="text-base font-semibold text-neutral-900 mb-1">No Requirements</h3>
                <p className="text-sm text-neutral-500">The team hasn't added requirements yet.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {project.roles.map((role, idx) => (
                  <RequirementModule 
                    key={role.id} 
                    role={role} 
                    index={idx}
                    notes={notes}
                    setNotes={setNotes}
                    expandedNotes={expandedNotes}
                    setExpandedNotes={setExpandedNotes}
                    submitting={submitting}
                    handleFeedback={handleFeedback}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ========== FOOTER ========== */}
          <div className="px-8 py-4 bg-neutral-50 border-t border-neutral-100">
            <div className="flex items-center justify-center gap-2 text-xs text-neutral-400">
              <img src={LOGO_URL} alt="" className="w-4 h-4 rounded opacity-60" />
              <span>Powered by <span className="font-medium text-neutral-500">TalentMeet</span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== REQUIREMENT MODULE ==========
function RequirementModule({ role, index, notes, setNotes, expandedNotes, setExpandedNotes, submitting, handleFeedback }) {
  const selectedInRole = role.candidates?.filter(c => c.client_status === "Selected").length || 0;
  
  return (
    <div>
      {/* Module Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-neutral-900">{role.role_name}</h2>
          {role.required_count && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 rounded">
              Need {role.required_count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedInRole > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded">
              {selectedInRole} Selected
            </span>
          )}
          <span className="px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-600 rounded">
            {role.candidates?.length || 0} Options
          </span>
        </div>
      </div>
      
      {/* Yellow Underline */}
      <div className="h-0.5 bg-gradient-to-r from-amber-400 to-amber-300 rounded-full mb-4" style={{ width: '60px' }}></div>

      {/* Candidates List */}
      {role.candidates?.length === 0 ? (
        <div className="text-center py-8 bg-neutral-50 rounded-xl">
          <Users className="w-5 h-5 text-neutral-300 mx-auto mb-2" />
          <p className="text-xs text-neutral-400">No options added</p>
        </div>
      ) : (
        <div className="space-y-2">
          {role.candidates.map(option => (
            <CandidateCard
              key={option.id}
              option={option}
              notes={notes}
              setNotes={setNotes}
              expandedNotes={expandedNotes}
              setExpandedNotes={setExpandedNotes}
              submitting={submitting}
              handleFeedback={handleFeedback}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ========== CANDIDATE CARD ==========
function CandidateCard({ option, notes, setNotes, expandedNotes, setExpandedNotes, submitting, handleFeedback }) {
  const optionType = option.option_type || "profile";
  const isExpanded = expandedNotes[option.id];
  const isSelected = option.client_status === "Selected";
  const isRejected = option.client_status === "Rejected";

  // Status Badge
  const StatusBadge = () => {
    if (isSelected) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded">
          <CheckCircle className="w-3 h-3" /> Selected
        </span>
      );
    }
    if (isRejected) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-500 rounded">
          <XCircle className="w-3 h-3" /> Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-500 rounded">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );
  };

  // Thumbnail - uses button/div instead of anchor to prevent router navigation
  const Thumbnail = () => {
    const baseClass = "w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden";
    
    if (optionType === "profile") {
      return (
        <div className={`${baseClass} bg-neutral-100`}>
          {option.profile_photo ? (
            <img src={option.profile_photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User className="w-6 h-6 text-neutral-400" />
            </div>
          )}
        </div>
      );
    }
    if (optionType === "image_gallery") {
      // Show actual image thumbnail if images exist
      const firstImage = option.images?.[0];
      const imageUrl = normalizeUploadUrl(firstImage);
      
      return (
        <button 
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (imageUrl) {
              window.open(imageUrl, '_blank', 'noopener,noreferrer');
            }
          }}
          className={`${baseClass} bg-violet-100 cursor-pointer hover:opacity-80 transition-opacity`}
          title="Click to view image"
        >
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={option.title || "Gallery"} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className={`w-full h-full items-center justify-center ${imageUrl ? 'hidden' : 'flex'}`}>
            <Image className="w-6 h-6 text-violet-600" />
          </div>
        </button>
      );
    }
    if (optionType === "pdf") {
      const pdfUrl = normalizeUploadUrl(option.pdf_url);
      return (
        <button 
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (pdfUrl) {
              window.open(pdfUrl, '_blank', 'noopener,noreferrer');
            }
          }}
          className={`${baseClass} bg-rose-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}
          title="Click to view PDF"
        >
          <FileText className="w-6 h-6 text-rose-600" />
        </button>
      );
    }
    if (optionType === "external_link") {
      return (
        <button 
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (option.external_url) {
              window.open(option.external_url, '_blank', 'noopener,noreferrer');
            }
          }}
          className={`${baseClass} bg-sky-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}
          title="Click to open link"
        >
          <Globe className="w-6 h-6 text-sky-600" />
        </button>
      );
    }
    return null;
  };

  return (
    <div 
      className={`bg-white border rounded-xl transition-all ${
        isSelected ? "border-green-200 bg-green-50/30" : 
        isRejected ? "border-neutral-200 opacity-50" : 
        "border-neutral-200 hover:border-neutral-300"
      }`}
      data-testid={`client-option-${option.id}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Thumbnail */}
          <Thumbnail />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              {/* Left: Info */}
              <div className="min-w-0 flex-1">
                {optionType === "profile" ? (
                  <>
                    <h3 className="font-semibold text-neutral-900 text-sm truncate">{option.profile_name}</h3>
                    <p className="text-xs text-neutral-500 mt-0.5 truncate">
                      {option.profile_category}
                      {option.profile_type && option.profile_type !== "talent" && (
                        <span className="ml-1 text-violet-600">• {option.profile_type}</span>
                      )}
                    </p>
                    {option.profile_location && (
                      <p className="text-xs text-neutral-400 mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {option.profile_location}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-neutral-900 text-sm truncate">{option.title}</h3>
                    {option.description && (
                      <p className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{option.description}</p>
                    )}
                  </>
                )}

                {/* Type-specific Actions */}
                <div className="mt-2 flex items-center gap-2">
                  {optionType === "profile" && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(`/p/${option.profile_slug}`, '_blank', 'noopener,noreferrer');
                      }}
                      className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
                    >
                      <Eye className="w-3 h-3" /> View Profile
                    </button>
                  )}
                  {optionType === "image_gallery" && option.images?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {option.images.slice(0, 3).map((img, i) => {
                          const imageUrl = normalizeUploadUrl(img);
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(imageUrl, '_blank', 'noopener,noreferrer');
                              }}
                              className="block"
                            >
                              <img 
                                src={imageUrl} 
                                alt={`Image ${i + 1}`} 
                                className="w-8 h-8 rounded object-cover hover:opacity-80 cursor-pointer border border-neutral-200"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="%239333ea" viewBox="0 0 24 24"><rect width="24" height="24" fill="%23f3e8ff" rx="4"/><path d="M4 16l4-4 4 4m4-4l4 4M14 10a2 2 0 100-4 2 2 0 000 4z" stroke="%239333ea" stroke-width="1.5" fill="none"/></svg>';
                                }}
                              />
                            </button>
                          );
                        })}
                        {option.images.length > 3 && (
                          <span className="w-8 h-8 rounded bg-neutral-100 flex items-center justify-center text-xs text-neutral-500 border border-neutral-200">
                            +{option.images.length - 3}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const imageUrl = normalizeUploadUrl(option.images[0]);
                          window.open(imageUrl, '_blank', 'noopener,noreferrer');
                        }}
                        className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 transition-colors font-medium"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                    </div>
                  )}
                  {optionType === "pdf" && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const pdfUrl = normalizeUploadUrl(option.pdf_url);
                        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700"
                    >
                      <Download className="w-3 h-3" /> Download PDF
                    </button>
                  )}
                  {optionType === "external_link" && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(option.external_url, '_blank', 'noopener,noreferrer');
                      }}
                      className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700"
                    >
                      <ExternalLink className="w-3 h-3" /> Open Link
                    </button>
                  )}
                </div>
              </div>

              {/* Right: Status + Actions */}
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <StatusBadge />
                
                {/* Action Buttons - Desktop */}
                <div className="hidden sm:flex items-center gap-1.5">
                  <button
                    onClick={() => setExpandedNotes(prev => ({ ...prev, [option.id]: !prev[option.id] }))}
                    className="px-2 py-1 text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    Notes
                  </button>
                  <button
                    onClick={() => handleFeedback(option.id, "Selected")}
                    disabled={submitting[option.id]}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      isSelected 
                        ? "bg-green-600 text-white" 
                        : "bg-amber-400 hover:bg-amber-500 text-neutral-900"
                    }`}
                    data-testid={`select-btn-${option.id}`}
                  >
                    Select
                  </button>
                  <button
                    onClick={() => handleFeedback(option.id, "Rejected")}
                    disabled={submitting[option.id]}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                      isRejected 
                        ? "bg-neutral-800 text-white" 
                        : "border border-neutral-300 text-neutral-600 hover:bg-neutral-100"
                    }`}
                    data-testid={`reject-btn-${option.id}`}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons - Mobile */}
            <div className="sm:hidden flex items-center justify-end gap-1.5 mt-3 pt-3 border-t border-neutral-100">
              <button
                onClick={() => setExpandedNotes(prev => ({ ...prev, [option.id]: !prev[option.id] }))}
                className="px-2 py-1 text-xs text-neutral-400 hover:text-neutral-600"
              >
                Notes
              </button>
              <button
                onClick={() => handleFeedback(option.id, "Selected")}
                disabled={submitting[option.id]}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                  isSelected ? "bg-green-600 text-white" : "bg-amber-400 text-neutral-900"
                }`}
                data-testid={`select-btn-mobile-${option.id}`}
              >
                Select
              </button>
              <button
                onClick={() => handleFeedback(option.id, "Rejected")}
                disabled={submitting[option.id]}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                  isRejected ? "bg-neutral-800 text-white" : "border border-neutral-300 text-neutral-600"
                }`}
                data-testid={`reject-btn-mobile-${option.id}`}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Notes */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0">
          <div className="bg-neutral-50 rounded-lg p-3">
            <Textarea
              placeholder="Add your notes..."
              value={notes[option.id] || ""}
              onChange={(e) => setNotes(prev => ({ ...prev, [option.id]: e.target.value }))}
              rows={2}
              className="text-xs bg-white border-neutral-200 focus:border-amber-400 focus:ring-amber-200"
              data-testid={`client-notes-${option.id}`}
            />
            <p className="text-xs text-neutral-400 mt-2">Notes are saved when you select or reject.</p>
          </div>
        </div>
      )}
    </div>
  );
}
